import * as XLSX from 'xlsx';
import type { CreateCompanyPayload } from '@/api/companies';

// CRM 企业字段 → 可接受的表头别名(小写、去空格后比较)。
// 按表头「名称」匹配列,而非硬编码位置 —— 文件列顺序任意都能对上,认不出的列忽略。
const COLUMN_ALIASES: Record<string, string[]> = {
  name: ['企业名称', '公司名称', '企业名单', '公司名', '单位名称', '名称', 'name'],
  creditCode: ['统一信用代码', '统一社会信用代码', '信用代码', '社会信用代码', 'creditcode'],
  address: ['地址', '公司地址', '注册地址', '办公地址', 'address'],
  website: ['网址', '官网', '网站', '链接', 'website', 'url'],
  type: ['类型', '企业类型', 'type'],
  responsiblePerson: ['负责人', '企业负责人', '负责人姓名', 'responsibleperson'],
  businessTags: ['业务标签', '业务', 'businesstags'],
  sourceTags: ['来源标签', '来源', 'sourcetags'],
  contactName: ['联系人', '联系人姓名', '联系人名称', 'contactname'],
  contactPhone: ['联系电话', '联系人电话', '电话', '手机', '手机号', 'contactphone', 'phone'],
  contactPosition: ['联系人职位', '职位', '职务', 'contactposition'],
};

const VALID_TYPES = ['实体企业', '认证机构', '第三方咨询机构'] as const;

export interface ParsedRow {
  index: number;
  name: string;
  creditCode: string;
  address: string;
  website: string;
  type: string;
  responsiblePerson: string;
  businessTags: string;
  sourceTags: string;
  contactName: string;
  contactPhone: string;
  contactPosition: string;
  valid: boolean;
  error?: string;
}

export interface ParseResult {
  rows: ParsedRow[];
  /** 表头识别到的字段(用于提示用户匹配了哪些列) */
  matchedFields: string[];
  /** 致命错误(如未识别到企业名称列)时非空,rows 为空 */
  fatalError?: string;
}

function norm(s: string): string {
  return s.replace(/\s+/g, '').replace(/[()（）]/g, '').toLowerCase();
}

/** 从表头行建立 field → 列索引 的映射。表头别名归一化后前缀匹配。 */
function buildHeaderMap(header: string[]): Record<string, number> {
  const map: Record<string, number> = {};
  header.forEach((cell, colIdx) => {
    const h = norm(String(cell ?? ''));
    if (!h) return;
    for (const [field, aliases] of Object.entries(COLUMN_ALIASES)) {
      if (field in map) continue; // 已匹配的字段不覆盖(取最先出现的列)
      if (aliases.some((a) => h === norm(a) || h.startsWith(norm(a)))) {
        map[field] = colIdx;
        break;
      }
    }
  });
  return map;
}

function cellAt(row: string[], idx: number | undefined): string {
  if (idx === undefined) return '';
  const v = row[idx];
  return v == null ? '' : String(v).trim();
}

function validate(row: ParsedRow): ParsedRow {
  const errors: string[] = [];
  // 智能匹配模式下必填仅「企业名称」,其余尽量导入。
  if (!row.name.trim()) errors.push('企业名称不能为空');
  if (row.type.trim() && !(VALID_TYPES as readonly string[]).includes(row.type.trim())) {
    errors.push(`类型"${row.type}"不合法`);
  }
  return { ...row, valid: errors.length === 0, error: errors.join('; ') };
}

/** 按表头名称智能匹配,把二维表格数据解析为预览行。第一行必须是表头。 */
export function parseRowsByHeader(allRows: string[][]): ParseResult {
  if (allRows.length < 2) {
    return { rows: [], matchedFields: [], fatalError: '文件内容不足,至少需要表头和一行数据' };
  }
  const header = allRows[0];
  const map = buildHeaderMap(header);

  if (map.name === undefined) {
    return {
      rows: [],
      matchedFields: [],
      fatalError: '未能识别到「企业名称」列。请确保文件首行包含表头(如:企业名称、统一信用代码、地址…)',
    };
  }

  const rows: ParsedRow[] = [];
  for (let i = 1; i < allRows.length; i++) {
    const r = allRows[i] || [];
    if (r.every((c) => !String(c ?? '').trim())) continue; // 跳过空行
    rows.push(
      validate({
        index: i,
        name: cellAt(r, map.name),
        creditCode: cellAt(r, map.creditCode),
        address: cellAt(r, map.address),
        website: cellAt(r, map.website),
        type: cellAt(r, map.type) || '实体企业',
        responsiblePerson: cellAt(r, map.responsiblePerson),
        businessTags: cellAt(r, map.businessTags),
        sourceTags: cellAt(r, map.sourceTags),
        contactName: cellAt(r, map.contactName),
        contactPhone: cellAt(r, map.contactPhone),
        contactPosition: cellAt(r, map.contactPosition),
        valid: true,
      }),
    );
  }
  return { rows, matchedFields: Object.keys(map) };
}

/** 解析 Excel(.xlsx/.xls)首个非空 sheet 的全部行(含表头),单元格统一转字符串。 */
export function readExcelRows(buffer: ArrayBuffer): string[][] {
  const workbook = XLSX.read(buffer, { type: 'array' });
  // 选第一个有数据的 sheet(多 sheet 文件常见空/杂 sheet)
  let best: string[][] = [];
  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName];
    // raw:false + defval:'' → 数字/日期转显示文本,空值填 ''(否则数字 cell 破坏下游 .trim)
    const json = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1, raw: false, defval: '' });
    const rows = json.map((row) => (row || []).map((c) => (c == null ? '' : String(c))));
    if (rows.length > best.length) best = rows;
  }
  return best;
}

/** 解析 CSV 文本为二维数组(含表头行)。 */
export function readCSVRows(text: string): string[][] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  return lines.map((line) => parseCSVLine(line));
}

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      result.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current.trim());
  return result;
}

/** 预览行(仅 valid)→ 批量导入 payload。 */
export function rowsToPayloads(rows: ParsedRow[]): CreateCompanyPayload[] {
  return rows
    .filter((r) => r.valid)
    .map((row) => ({
      name: row.name.trim(),
      creditCode: row.creditCode.trim(),
      address: row.address.trim(),
      website: row.website.trim(),
      type: (VALID_TYPES as readonly string[]).includes(row.type.trim())
        ? (row.type.trim() as CreateCompanyPayload['type'])
        : '实体企业',
      responsiblePerson: row.responsiblePerson.trim(),
      businessTags: splitTags(row.businessTags),
      sourceTags: splitTags(row.sourceTags),
      contacts: row.contactName.trim()
        ? [{ name: row.contactName.trim(), phone: row.contactPhone.trim(), position: row.contactPosition.trim() }]
        : [],
      businessDemands: [],
    }));
}

function splitTags(s: string): string[] {
  return s
    .split(/[;；,，]/)
    .map((t) => t.trim())
    .filter(Boolean);
}
